import React from 'react';
import classnames from 'classnames';
import {
  parseMetadata,
  suffixMetadata,
  PropsWithMetadata,
} from '@highlight-ui/utils-commons';
import { InteractiveIcon } from '@highlight-ui/icon';
import { Body } from '@highlight-ui/typography';
import styles from './SelectChip.module.scss';

export type SelectChipProps = PropsWithMetadata<{
  /**
   * Sets the SelectChip's text.
   */
  children: string;
  /**
   * Disables the SelectChip. Causes the `onRemove` prop to be ignored when this prop is set to `true`.
   */
  disabled?: boolean;
  /**
   * Sets a callback for when the SelectChip is removed. Ignored when `disabled` is set to `true`.
   *
   * A removal event can happen when the SelectChip's remove icon is clicked or
   * when the SelectChip has focus and `Delete` or `Backspace` is pressed on the keyboard.
   */
  onRemove?: (
    e: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => void;
  /**
   * Sets a callback for when other keyboard keys are used on the SelectChip. Ignored when `disabled` is set to `true`.
   *
   * `onKeyDown` needs `onRemove` and/or `onClick` props to work.
   *
   * `onKeyDown` for this component doesn't handle `Delete` or `Backspace` keys which are handled by the `onRemove`
   * prop
   * @see onRemove
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Sets a callback for when the SelectChip is clicked. Ignored when `disabled` is set to `true`.
   *
   * A click event can happen when the SelectChip is clicked with a mouse pointer.
   */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Allows providing a custom class name
   */
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  /**
   * Allows providing a custom tabIndex number
   */
  tabIndex?: React.HTMLAttributes<HTMLDivElement>['tabIndex'];
}>;

const SelectChip = React.forwardRef(
  (
    {
      children,
      disabled = false,
      onRemove,
      onKeyDown,
      onClick,
      className,
      metadata,
      tabIndex = 0,
    }: SelectChipProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const isRemovable = !disabled && onRemove !== undefined;
    const isClickable = !disabled && onClick !== undefined;

    const chipAttributes: React.ComponentPropsWithoutRef<'div'> =
      isRemovable || isClickable
        ? {
            role: 'button',
            tabIndex,
            onKeyDown: (event) => {
              switch (event.key) {
                case 'Delete':
                case 'Backspace':
                  if (onRemove) {
                    onRemove(event);
                  }
                  break;

                default:
                  if (onKeyDown) {
                    onKeyDown(event);
                  }
                  break;
              }
            },
            onClick,
          }
        : {};

    return (
      <div
        {...parseMetadata(metadata)}
        {...chipAttributes}
        ref={ref}
        className={classnames(
          styles.selectChip,
          {
            [styles.clickableSelectChip]: isClickable,
            [styles.removableSelectChip]: isRemovable,
          },
          className,
        )}
        aria-disabled={disabled}
      >
        <Body color={disabled ? 'text-disabled' : 'text-default'}>
          {children}
        </Body>

        {isRemovable && (
          <InteractiveIcon
            onClick={onRemove}
            metadata={suffixMetadata(metadata, 'remove-icon')}
            iconName="times"
            iconSize={12}
            tabIndex={-1}
            aria-hidden
          />
        )}
      </div>
    );
  },
);

export default SelectChip;
